import React, { useState, useRef, useCallback } from 'react';

/**
 * Hand-rolled SVG line — the house chart. Orange is always "you"
 * (portfolio, drawn conviction); ink is the market (asset history).
 *
 * Scrubbable: drag a cursor or finger along the line to read the value and
 * date at any point (crosshair + label, Robinhood-style). touch-action keeps
 * vertical page scroll alive while horizontal scrubs are captured.
 */
const MarketLine = ({
  points,
  height = 180,
  width = 700,
  color = 'var(--ft-brand)',
  fill = 'rgba(255,122,0,0.05)',
  baseline = null,
  ariaLabel = 'Value over time',
  formatValue = (v) => Math.round(v).toLocaleString('en-US'),
}) => {
  const svgRef = useRef(null);
  const [scrubIdx, setScrubIdx] = useState(null);

  const handleMove = useCallback(
    (e) => {
      const svg = svgRef.current;
      if (!svg || !points || points.length < 2) return;
      const rect = svg.getBoundingClientRect();
      const clientX = e.clientX !== undefined ? e.clientX : e.touches && e.touches[0]?.clientX;
      if (clientX === undefined) return;
      const vx = ((clientX - rect.left) / rect.width) * width;
      const pad = 16;
      const plotW = width - pad * 2;
      const idx = Math.round(((vx - pad) / plotW) * (points.length - 1));
      setScrubIdx(Math.max(0, Math.min(points.length - 1, idx)));
    },
    [points, width]
  );

  const clearScrub = useCallback(() => setScrubIdx(null), []);

  if (!points || points.length === 0) return null;
  // A one-point series draws as a flat line, not a wedge.
  if (points.length === 1) points = [points[0], points[0]];

  const pad = 16;
  const values = points.map((p) => p.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (baseline !== null) {
    min = Math.min(min, baseline);
    max = Math.max(max, baseline);
  }
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const span = max - min;
  const plotW = width - pad * 2;
  const plotH = height - pad * 2;
  const xOf = (i) => pad + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yOf = (v) => pad + (1 - (v - min) / span) * plotH;

  const coords = points.map((p, i) => `${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`);
  const last = points[points.length - 1];

  const scrub = scrubIdx !== null ? points[scrubIdx] : null;
  const scrubX = scrub ? xOf(scrubIdx) : null;
  const scrubDate = scrub && scrub.date
    ? new Date(`${scrub.date}T12:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  // Keep the label inside the plot near the edges.
  const labelAnchor = scrubX === null ? 'middle' : scrubX < width * 0.18 ? 'start' : scrubX > width * 0.82 ? 'end' : 'middle';

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      style={{ touchAction: 'pan-y', cursor: 'crosshair' }}
      onPointerMove={handleMove}
      onPointerDown={handleMove}
      onPointerLeave={clearScrub}
      onPointerUp={clearScrub}
    >
      {fill && (
        <polygon
          fill={fill}
          stroke="none"
          points={`${coords.join(' ')} ${xOf(points.length - 1).toFixed(1)},${height - pad} ${pad},${height - pad}`}
        />
      )}
      {baseline !== null && (
        <line
          x1={pad}
          y1={yOf(baseline)}
          x2={width - pad}
          y2={yOf(baseline)}
          stroke="var(--ft-hairline)"
          strokeWidth="1"
          strokeDasharray="2 5"
        />
      )}
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={xOf(points.length - 1)}
        cy={yOf(last.value)}
        r="4.5"
        fill={color}
        opacity={scrub ? 0.35 : 1}
      />
      {scrub && (
        <g pointerEvents="none">
          <line
            x1={scrubX}
            y1={pad - 2}
            x2={scrubX}
            y2={height - pad}
            stroke="var(--ft-ink-3)"
            strokeWidth="1"
          />
          <circle cx={scrubX} cy={yOf(scrub.value)} r="4.5" fill="var(--ft-ink)" />
          <text
            x={scrubX}
            y={11}
            textAnchor={labelAnchor}
            style={{
              fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
              fontSize: 12,
              fontWeight: 700,
              fill: 'var(--ft-ink)',
            }}
          >
            {formatValue(scrub.value)}
            {scrubDate ? (
              <tspan style={{ fontWeight: 400, fill: 'var(--ft-ink-3)' }}>{`  ${scrubDate}`}</tspan>
            ) : null}
          </text>
        </g>
      )}
    </svg>
  );
};

export default MarketLine;
